import type {
  AuthenticationTokens,
  RegisterResetPasswordRequest,
  ResetPasswordRequest,
} from '@applied/proto/web_services/candidate_experience/proto/authentication_pb';
import { didRequestSucceed, getData, postData } from 'api/http_requests';

/**
 * A request used to verify a user's registration token and registration status.
 *
 * @param registrationToken The registration token to use to authenticate the request.
 * @returns If the request was successful or not.
 */
export const getRegistrationStatus = async (registrationToken: string): Promise<boolean> => {
  return didRequestSucceed(async () => {
    return getData(`/api/register/registration_status?registration_token=${registrationToken}`);
  });
};

/**
 * A request used to reset a user's password for the first time.
 *
 * @param password The password to set for the first-time user.
 * @param registrationToken The registration token to use to authenticate the request.
 * @returns If the request was successful or not.
 */
export const postRegisterResetPassword = async (
  password: string,
  registrationToken: string,
): Promise<void> => {
  const payload: RegisterResetPasswordRequest = {
    password,
    registrationToken,
  };
  await postData(`/api/register/reset_password`, payload);
};

/**
 * A request used to reset a user's password after the first time.
 *
 * @param password The password to set for the first-time user.
 * @param authenticationTokens The authentication tokens to use to authenticate the request.
 * @returns If the request was successful or not.
 */
export const postResetPassword = async (
  password: string,
  authenticationTokens: AuthenticationTokens,
): Promise<void> => {
  const payload: ResetPasswordRequest = {
    password,
    tokens: authenticationTokens,
  };
  await postData(`/api/auth/reset_password`, payload);
};
